import { useState } from 'react'
import type { FieldDefinition } from '@/api/types'

/** Persisted settings for the board's table view (localStorage 'pp.tableSettings'). */

export const TABLE_SETTINGS_STORAGE_KEY = 'pp.tableSettings'

export const PAGE_SIZES = [25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

export type RowHeight = 'default' | 'compact'

/** Prefix for deal custom-field column ids, e.g. 'custom.bedrooms'. */
export const CUSTOM_COLUMN_PREFIX = 'custom.'

/** A column id is a built-in key ('name', 'stage', …) or a 'custom.<fieldKey>'. */
export type ColumnId = string

export interface ColumnDef {
  id: ColumnId
  label: string
  /** Locked columns (deal name) are always visible and can't be removed. */
  locked?: boolean
}

/** Built-in column pool in canonical order. Custom columns are appended per agency at runtime. */
export const COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Deal name', locked: true },
  { id: 'photo', label: 'Photo' },
  { id: 'stage', label: 'Stage' },
  { id: 'nextContactDate', label: 'Next contact date' },
  { id: 'value', label: 'Value' },
  { id: 'owner', label: 'Owner' },
  { id: 'created', label: 'Created' },
]

/** Deal custom fields as column defs ('custom.<key>' ids, labels from definitions). */
export function customTableColumns(defs: FieldDefinition[] | undefined): ColumnDef[] {
  return (defs ?? []).map((d) => ({ id: CUSTOM_COLUMN_PREFIX + d.fieldKey, label: d.label }))
}

export interface TableSettings {
  pageSize: PageSize
  rowHeight: RowHeight
  zebra: boolean
  /** Visible column ids in canonical order; always contains 'name'. */
  columns: ColumnId[]
  /** Settings version: 2 = saved since the Photo column exists (older ones get it switched on once). */
  v?: number
}

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  pageSize: 25,
  rowHeight: 'default',
  zebra: false,
  columns: ['name', 'photo', 'stage', 'nextContactDate', 'value', 'owner'],
  v: 2,
}

/** Coerces parsed JSON of unknown shape to valid settings, falling back per field. */
function normalize(raw: unknown): TableSettings {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const pageSize = (PAGE_SIZES as readonly number[]).includes(obj.pageSize as number)
    ? (obj.pageSize as PageSize)
    : DEFAULT_TABLE_SETTINGS.pageSize
  const rowHeight: RowHeight = obj.rowHeight === 'compact' ? 'compact' : 'default'
  const requested = Array.isArray(obj.columns)
    ? (obj.columns as unknown[])
    : DEFAULT_TABLE_SETTINGS.columns
  // Preserve the user's column ORDER; keep built-in ids or 'custom.<key>' (custom columns whose
  // definition was deleted are dropped at render time). 'name' is locked — always present and first.
  const builtinIds = new Set(COLUMNS.map((c) => c.id))
  const kept: string[] = []
  for (const id of requested) {
    if (typeof id !== 'string' || kept.includes(id)) continue
    if (!id.startsWith(CUSTOM_COLUMN_PREFIX) && !builtinIds.has(id)) continue
    kept.push(id)
  }
  // Settings saved before the Photo column existed show it once (it can then be switched off for good).
  if (obj.v !== 2 && !kept.includes('photo')) kept.unshift('photo')
  const columns = ['name', ...kept.filter((c) => c !== 'name')]
  return { pageSize, rowHeight, zebra: obj.zebra === true, columns, v: 2 }
}

export function loadTableSettings(): TableSettings {
  try {
    const raw = localStorage.getItem(TABLE_SETTINGS_STORAGE_KEY)
    if (!raw) return DEFAULT_TABLE_SETTINGS
    return normalize(JSON.parse(raw))
  } catch {
    return DEFAULT_TABLE_SETTINGS
  }
}

export function saveTableSettings(settings: TableSettings) {
  localStorage.setItem(TABLE_SETTINGS_STORAGE_KEY, JSON.stringify(settings))
}

/** Table-view settings backed by localStorage: [settings, update]. */
export function useTableSettings(): [TableSettings, (next: TableSettings) => void] {
  const [settings, setSettings] = useState<TableSettings>(loadTableSettings)
  function update(next: TableSettings) {
    setSettings(next)
    saveTableSettings(next)
  }
  return [settings, update]
}
